import { computed, ComputedRef, Ref } from "vue";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { IEarlyDriveData } from "@/domain/entities/lineInfo";
import { LineScheduleDisplay } from "@/types";

export const useDriveDatesItems = (
  earlyDriveInfosList: Ref<IEarlyDriveData[][]>
): { driveDatesItems: ComputedRef<LineScheduleDisplay[]> } => {
  const driveDatesItems = computed<LineScheduleDisplay[]>(() => {
    if (earlyDriveInfosList.value.every((date) => !date.length)) return [];

    const todayWeekday = getTodayWeekday();
    const uniqueDaySets = getUniqueDaySets(earlyDriveInfosList.value);

    return uniqueDaySets.map((daysSet) =>
      getDisplayInfo(daysSet, todayWeekday)
    );
  });

  // 중복을 제거한 요일 집합 생성
  const getUniqueDaySets = (
    earlyDriveInfosList: IEarlyDriveData[][]
  ): Set<string>[] => {
    const uniqueDaySets = new Set<string>();
    const daySets: Set<string>[] = [];

    for (const round of earlyDriveInfosList) {
      if (!round.length) continue;
      const days = round
        .map((item) => item.weekdayName)
        .filter(Boolean)
        .sort();
      const key = days.join(",");
      if (!uniqueDaySets.has(key)) {
        uniqueDaySets.add(key);
        daySets.push(new Set(days));
      }
    }
    return daySets;
  };

  // 현재 요일 가져오기
  const getTodayWeekday = (): string => dayjs().locale("ko").format("ddd");

  // 2중 배열을 평탄화하여 단일 요일 집합 생성
  const getFlattenedDriveDaysSet = (
    rounds: IEarlyDriveData[][]
  ): Set<string> => {
    const daysSet = new Set<string>();
    rounds.flat().forEach((item) => {
      if (item.weekdayName && item.weekdayName.trim() !== "") {
        daysSet.add(item.weekdayName);
      }
    });
    return daysSet;
  };

  // 표시 정보 생성
  const getDisplayInfo = (
    daysSet: Set<string>,
    todayWeekday: string
  ): LineScheduleDisplay => {
    const sortedDays = sortAndSplitWeekdays(daysSet, todayWeekday);
    const weekends = ["토", "일"];
    const weekdays = ["월", "화", "수", "목", "금"];
    const allDays = ["월", "화", "수", "목", "금", "토", "일"];

    // 주말
    if (sortedDays.length === 2 && weekends.every((day) => daysSet.has(day))) {
      return {
        days: [{ text: "주말", isToday: sortedDays.some((d) => d.isToday) }],
      };
    }

    // 평일 (월 ~ 금)
    if (sortedDays.length === 5 && weekdays.every((day) => daysSet.has(day))) {
      return {
        days: [{ text: "평일", isToday: sortedDays.some((d) => d.isToday) }],
      };
    }

    // 매일 (월 ~ 일)
    if (sortedDays.length === 7 && allDays.every((day) => daysSet.has(day))) {
      return { days: [{ text: "매일", isToday: true }] };
    }

    return { days: sortedDays };
  };

  // 요일 정렬 및 개별 요일 객체 생성
  const sortAndSplitWeekdays = (
    daysSet: Set<string>,
    todayWeekday: string,
    weekDays: string[] = ["월", "화", "수", "목", "금", "토", "일"]
  ): { text: string; isToday: boolean }[] => {
    return weekDays
      .filter((day) => daysSet.has(day))
      .map((day) => ({ text: day, isToday: day === todayWeekday }));
  };

  return { driveDatesItems };
};
